import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import './Profile.scss';
import api from '../Axios/axios';

const roleLabels = {
  Owner: 'Y\u00f6netici',
  Employee: '\u00c7al\u0131\u015fan',
  Company: 'Firma Kullan\u0131c\u0131s\u0131',
  Partner: 'Partner',
  Accountant: 'Muhasebe',
  RentalPersonnel: 'Kiralama Personeli',
  TechnicalServiceManager: 'Yetkili Servis M\u00fcd\u00fcr\u00fc',
  TechnicalServicePersonnel: 'Yetkili Servis Personeli',
};

const getUserFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    return {
      token,
      role: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
      userId: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
      companyId: decoded.CompanyId,
      userName: decoded.UserName,
      tc: decoded.TC || decoded.tc || '',
    };
  } catch (error) {
    console.error('Token \u00e7\u00f6z\u00fcmleme hatas\u0131:', error);
    return null;
  }
};

const splitFullName = (fullName) => {
  if (!fullName) {
    return { name: '', surname: '' };
  }

  const parts = fullName.trim().split(/\s+/);
  return {
    name: parts[0] || '',
    surname: parts.slice(1).join(' '),
  };
};

const firstDefinedValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      if (normalizedValue !== '') {
        return normalizedValue;
      }
    }
  }
  return '';
};

const emptyUserProfile = {
  id: '',
  name: '',
  surname: '',
  email: '',
  phoneNumber: '',
  tc: '',
  TC: '',
  role: '',
};

const emptyCompanyProfile = {
  id: '',
  name: '',
  email: '',
  address: '',
  taxNo: '',
  authorizedPerson1: '',
  phoneNumber1: '',
  authorizedPerson2: '',
  phoneNumber2: '',
};

const emptyPasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const withTimeout = (promise, timeoutMs = 8000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('PROFILE_REQUEST_TIMEOUT')), timeoutMs)
    ),
  ]);

const Profile = () => {
  const session = useMemo(() => getUserFromToken(), []);
  const isCompany = session?.role === 'Company';
  const sessionNameParts = useMemo(() => splitFullName(session?.userName), [session?.userName]);
  const phoneCacheKey = useMemo(
    () => (isCompany ? `profile_company_phone_${session?.companyId || ''}` : `profile_phone_${session?.userId || ''}`),
    [isCompany, session?.companyId, session?.userId]
  );

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [profileData, setProfileData] = useState(isCompany ? emptyCompanyProfile : emptyUserProfile);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);

  const fetchProfile = async ({ keepLoadingState = false } = {}) => {
    if (!session?.token) {
      setErrorMessage('Oturum bilgisi bulunamad\u0131.');
      setLoading(false);
      return;
    }

    if (!keepLoadingState) {
      setLoading(true);
    }

    try {
      const headers = {
        Authorization: `Bearer ${session.token}`,
      };

      const requests = isCompany
        ? [
            withTimeout(api.get('/Company/Me', { headers })),
            withTimeout(api.get(`/Company/GetById?id=${session.companyId}`, { headers })),
          ]
        : [
            withTimeout(api.get('/User/Me', { headers })),
            withTimeout(api.get(`/User/GetByUserId?userId=${session.userId}`, { headers })),
          ];

      const results = await Promise.allSettled(requests);
      const successfulData = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value?.data?.data)
        .filter(Boolean);

      if (!isCompany && session?.companyId) {
        try {
          const usersResponse = await withTimeout(
            api.get(`/User?tenantId=${session.companyId}`, { headers }),
            5000
          );
          const tenantUsers = usersResponse?.data?.data || [];
          const currentUser = tenantUsers.find(
            (item) =>
              item?.id === session.userId ||
              item?.Id === session.userId ||
              item?.email === session.email ||
              item?.Email === session.email
          );

          if (currentUser) {
            successfulData.push(currentUser);
          }
        } catch (userListError) {
          console.error('Kullan\u0131c\u0131 listesi fallback al\u0131namad\u0131:', userListError);
        }
      }

      if (!successfulData.length) {
        throw new Error('PROFILE_DATA_UNAVAILABLE');
      }

      const pickField = (...keys) =>
        firstDefinedValue(
          ...successfulData.flatMap((item) => keys.map((key) => item?.[key]))
        );

      const incomingData = isCompany
        ? {
            ...emptyCompanyProfile,
            id: pickField('id', 'Id'),
            name: pickField('name', 'Name'),
            email: pickField('email', 'Email'),
            address: pickField('address', 'Address'),
            taxNo: pickField('taxNo', 'TaxNo'),
            authorizedPerson1: pickField('authorizedPerson1', 'AuthorizedPerson1'),
            phoneNumber1: pickField('phoneNumber1', 'PhoneNumber1'),
            authorizedPerson2: pickField('authorizedPerson2', 'AuthorizedPerson2'),
            phoneNumber2: pickField('phoneNumber2', 'PhoneNumber2'),
          }
        : {
            ...emptyUserProfile,
            id: pickField('id', 'Id'),
            name: pickField('name', 'Name'),
            surname: pickField('surname', 'Surname'),
            email: pickField('email', 'Email'),
            phoneNumber: pickField('phoneNumber', 'PhoneNumber'),
            tc: pickField('tc', 'TC', 'tC', 'Tc'),
            role: pickField('role', 'Role'),
          };

      setProfileData({
        ...(isCompany ? emptyCompanyProfile : emptyUserProfile),
        ...incomingData,
        name: firstDefinedValue(incomingData?.name, incomingData?.Name, sessionNameParts.name),
        surname: firstDefinedValue(incomingData?.surname, incomingData?.Surname, sessionNameParts.surname),
        email: firstDefinedValue(incomingData?.email, incomingData?.Email, session?.email),
        phoneNumber: firstDefinedValue(
          incomingData?.phoneNumber,
          incomingData?.PhoneNumber,
          !isCompany ? localStorage.getItem(phoneCacheKey) : ''
        ),
        phoneNumber1: firstDefinedValue(
          incomingData?.phoneNumber1,
          incomingData?.PhoneNumber1,
          isCompany ? localStorage.getItem(phoneCacheKey) : ''
        ),
        tc: firstDefinedValue(incomingData?.tc, incomingData?.TC, incomingData?.tC, incomingData?.Tc, session?.tc),
      });
      const resolvedPhoneValue = !isCompany
        ? firstDefinedValue(incomingData?.phoneNumber, incomingData?.PhoneNumber)
        : firstDefinedValue(incomingData?.phoneNumber1, incomingData?.PhoneNumber1);

      if (resolvedPhoneValue) {
        localStorage.setItem(phoneCacheKey, resolvedPhoneValue);
      }
      setErrorMessage('');
    } catch (error) {
      console.error('Profil verisi al\u0131namad\u0131:', error);
      setErrorMessage('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [isCompany, session, sessionNameParts.name, sessionNameParts.surname]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getApiMessages = (error, fallback) => {
    const messages = error?.response?.data?.messages;
    if (Array.isArray(messages) && messages.length) {
      return messages.join(' ');
    }
    return fallback;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!session?.token) return;

    setSavingProfile(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const headers = {
        Authorization: `Bearer ${session.token}`,
      };

      if (isCompany) {
        const companyPayload = {
          id: profileData.id,
          name: profileData.name || '',
          email: profileData.email || '',
          address: profileData.address || '',
          taxNo: profileData.taxNo || '',
          authorizedPerson1: profileData.authorizedPerson1 || '',
          phoneNumber1: profileData.phoneNumber1 || '',
          authorizedPerson2: profileData.authorizedPerson2 || '',
          phoneNumber2: profileData.phoneNumber2 || '',
        };

        try {
          await api.put('/Company/Me', companyPayload, { headers });
        } catch (primaryError) {
          await api.put('/Company', companyPayload, { headers });
        }
        localStorage.setItem(phoneCacheKey, companyPayload.phoneNumber1 || '');
      } else {
        const userPayload = {
          id: profileData.id || session.userId,
          name: profileData.name || sessionNameParts.name || '',
          surname: profileData.surname || sessionNameParts.surname || '',
          email: profileData.email || session.email || '',
          phoneNumber: profileData.phoneNumber || '',
          TC: profileData.tc || profileData.TC || session.tc || '',
        };

        try {
          await api.put('/User/Me', userPayload, { headers });
        } catch (primaryError) {
          await api.put('/User', userPayload, { headers });
        }
        localStorage.setItem(phoneCacheKey, userPayload.phoneNumber || '');
      }

      setProfileData((prev) => ({
        ...prev,
        phoneNumber: profileData.phoneNumber || prev.phoneNumber || '',
        phoneNumber1: profileData.phoneNumber1 || prev.phoneNumber1 || '',
        phoneNumber2: profileData.phoneNumber2 || prev.phoneNumber2 || '',
      }));
      setSuccessMessage('Profil bilgileri ba\u015far\u0131yla g\u00fcncellendi.');
    } catch (error) {
      console.error('Profil g\u00fcncelleme hatas\u0131:', error);
      setErrorMessage(getApiMessages(error, 'Profil g\u00fcncellenemedi.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!session?.token) return;

    setSavingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      await api.post(
        '/Auth/ChangePassword',
        passwordForm,
        {
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        }
      );

      setPasswordSuccess('\u015eifre ba\u015far\u0131yla de\u011fi\u015ftirildi.');
      setPasswordForm(emptyPasswordForm);
    } catch (error) {
      console.error('Sifre degistirme hatasi:', error);
      setPasswordError(getApiMessages(error, '\u015eifre de\u011fi\u015ftirilemedi.'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-edit-page">
        <Navbar />
        <div className="profile-layout">
          <div className="profile-card loading-card">Profil bilgileri yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-edit-page">
      <Navbar />
      <div className="profile-layout">
        <div className="profile-header">
          <h1>Profil Düzenle</h1>
        </div>

        <div className="profile-grid">
          <section className="profile-card profile-summary-card">
            <h2>Hesap Özeti</h2>
            <div className="summary-list">
              <div className="summary-item">
                <span>Rol</span>
                <strong>{roleLabels[session?.role] || session?.role || '-'}</strong>
              </div>
              <div className="summary-item">
                <span>Oturum E-postası</span>
                <strong>{session?.email || profileData.email || '-'}</strong>
              </div>
              {!isCompany && (
                <div className="summary-item">
                  <span>Kullanıcı</span>
                  <strong>{session?.userName || `${profileData.name || ''} ${profileData.surname || ''}`.trim() || '-'}</strong>
                </div>
              )}
              <div className="summary-note">
                Rol, yetki ve sistem tanımları bu ekrandan değiştirilemez.
              </div>
            </div>
          </section>

          <section className="profile-card">
            <h2>{isCompany ? 'Firma Bilgileri' : 'Kişisel Bilgiler'}</h2>
            <form onSubmit={handleProfileSubmit} className="profile-form">
              {isCompany ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Firma Adı</label>
                      <input id="name" name="name" value={profileData.name || ''} onChange={handleProfileChange} readOnly disabled />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Firma E-Posta</label>
                      <input id="email" name="email" type="email" value={profileData.email || ''} onChange={handleProfileChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="address">Adres</label>
                      <input id="address" name="address" value={profileData.address || ''} onChange={handleProfileChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="taxNo">Vergi No</label>
                      <input id="taxNo" name="taxNo" value={profileData.taxNo || ''} onChange={handleProfileChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="authorizedPerson1">Yetkili Kişi 1</label>
                      <input id="authorizedPerson1" name="authorizedPerson1" value={profileData.authorizedPerson1 || ''} onChange={handleProfileChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phoneNumber1">Telefon 1</label>
                      <input id="phoneNumber1" name="phoneNumber1" value={profileData.phoneNumber1 || ''} onChange={handleProfileChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="authorizedPerson2">Yetkili Kişi 2</label>
                      <input id="authorizedPerson2" name="authorizedPerson2" value={profileData.authorizedPerson2 || ''} onChange={handleProfileChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phoneNumber2">Telefon 2</label>
                      <input id="phoneNumber2" name="phoneNumber2" value={profileData.phoneNumber2 || ''} onChange={handleProfileChange} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Ad</label>
                      <input id="name" name="name" value={profileData.name || sessionNameParts.name || ''} onChange={handleProfileChange} readOnly disabled />
                    </div>
                    <div className="form-group">
                      <label htmlFor="surname">Soyad</label>
                      <input id="surname" name="surname" value={profileData.surname || sessionNameParts.surname || ''} onChange={handleProfileChange} readOnly disabled />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">E-Posta</label>
                      <input id="email" name="email" type="email" value={profileData.email || session?.email || ''} onChange={handleProfileChange} readOnly disabled />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phoneNumber">Telefon</label>
                      <input id="phoneNumber" name="phoneNumber" value={profileData.phoneNumber || ''} onChange={handleProfileChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="tc">TC Kimlik No</label>
                      <input id="tc" name="tc" value={profileData.tc || profileData.TC || ''} onChange={handleProfileChange} readOnly disabled />
                    </div>
                    <div className="form-group form-group-readonly">
                      <label>Rol</label>
                      <input value={roleLabels[session?.role] || session?.role || '-'} disabled readOnly />
                    </div>
                  </div>
                </>
              )}

              {errorMessage && <div className="form-message error">{errorMessage}</div>}
              {successMessage && <div className="form-message success">{successMessage}</div>}

              <button type="submit" className="primary-btn" disabled={savingProfile}>
                {savingProfile ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
              </button>
            </form>
          </section>

          <section className="profile-card password-card">
            <h2>{'\u015eifre De\u011fi\u015ftir'}</h2>
            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-row single-column">
                <div className="form-group">
                  <label htmlFor="currentPassword">{'Mevcut \u015eifre'}</label>
                  <input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="newPassword">{'Yeni \u015eifre'}</label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">{'Yeni \u015eifre Tekrar'}</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                  />
                </div>
              </div>

              {passwordError && <div className="form-message error">{passwordError}</div>}
              {passwordSuccess && <div className="form-message success">{passwordSuccess}</div>}

              <button type="submit" className="primary-btn" disabled={savingPassword}>
                {savingPassword ? 'G\u00fcncelleniyor...' : '\u015eifreyi G\u00fcncelle'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;

